// @ts-strict-ignore
export const filterEmptyObjectsFromEntries = ([, value]) => (
  typeof value !== 'object'
  || value === null
  || Array.isArray(value)
  || (value && Object.keys(value).length > 0)
);

export const formatArrayText = array => `[${array.map(value => {
  if (value && typeof value === 'object') {
    return Array.isArray(value)
      ? formatArrayText(value)
      : JSON.stringify(value);
  }
  return value;
}).join(', ') || ' '}]`;

export const formatCheckboxValues = value => {
  if (typeof value === 'boolean') return String(value);
  if (value === null || value === '') return '—';
  return value;
};

// we want to render a collapse if the value is an object,
// or if it's an array with an object nested somewhere within it
// if someone nests an object deeper than 99 levels into an array please contact the FBI immediately
export const shouldBeCollapsible = value => (
  value
  && typeof value === 'object'
  && (!Array.isArray(value) || value.flat(99).some(arrayItem => arrayItem && typeof arrayItem === 'object' && !Array.isArray(arrayItem)))
);

export const getCollapseChecked = (values, whitelist) => {
  // default these both to true, and adjust accordingly as we traverse the structure
  let allChecked = true;
  let allUnchecked = true;
  const checkChildren = (childValues, childWhitelist) => {
    const childKeys = Object.keys(childValues);
    childKeys.forEach(key => {
      // if we hit a nested object, recurse into it
      if (
        (allChecked || allUnchecked) // skip recursion if we already know it's indeterminate
        && childWhitelist[key]
        && childValues[key]
        && typeof childWhitelist[key] === 'object'
        && typeof childValues[key] === 'object') {
        checkChildren(childValues[key], childWhitelist[key]);
        // don't bother checking and setting `allChecked = false` if we've already found a missing check
      } else if (
        allChecked
        && !(
          childValues[key]
          && typeof childValues[key] === 'object'
          && !Array.isArray(childValues[key])
          && !Object.keys(childValues[key]).length
        )
        && !childWhitelist[key]
      ) {
        allChecked = false;
        // don't bother checking and setting `allUnchecked = false` if we've already found a checked box
      } else if (allUnchecked && childWhitelist[key]) {
        allUnchecked = false;
      }
    });
  };
  checkChildren(values, whitelist);
  if (allChecked) return 'checked';
  if (allUnchecked) return 'unchecked';
  return 'indeterminate';
};

export const checkAllChildren = (values, setWhitelist, checked) => {
  if (!checked) {
    setWhitelist({});
  } else {
    const createNewWhitelist = childValues => (
      Object.entries(childValues).reduce((whitelist, [key, value]) => {
        const newWhitelist = { ...whitelist };
        // if it's a value with children, recurse
        if (shouldBeCollapsible(value)) {
          newWhitelist[key] = createNewWhitelist(value);
        } else {
          newWhitelist[key] = true;
        }
        return newWhitelist;
      }, {})
    );
    const whitelistAllChecked = createNewWhitelist(values);
    setWhitelist(whitelistAllChecked);
  }
};

export const countChildren: (data: object) => number = data => {
  let total = 0;
  Object.values(data || {}).forEach(value => {
    if (shouldBeCollapsible(value)) {
      total += countChildren(value);
    } else {
      total += 1;
    }
  });
  return total;
};

export const countSelected: (
  whitelistValues: object | boolean | string,
  data: object
) => number = (whitelistValues, data) => {
  if (!data) return 0;
  if (whitelistValues === true || whitelistValues === 'top') return countChildren(data);
  let total = 0;
  Object.entries(whitelistValues || {}).forEach(([key, value]) => {
    const dataIsCollapsible = shouldBeCollapsible(data?.[key]);
    if ((value === true || value === 'top') && !dataIsCollapsible && data?.[key] !== undefined) {
      total += 1;
    } else {
      total += countSelected(value, data?.[key]);
    }
  });
  return total;
};

export const initializeTabCounts: (
  tabs: string[],
  whitelist: object,
  data: object,
) => { [tab: string]: number } = (tabs, whitelist, data) => {
  const initialTabCounts = Object.fromEntries(tabs.map(tab => ([tab, 0])));
  Object.entries(whitelist || {}).forEach(([tab, tabWhitelist]) => {
    initialTabCounts[tab] = countSelected(tabWhitelist, data?.[tab]);
  });
  return initialTabCounts;
};
